<template>
	<NuxtLayout>
		<Suspense>
			<NuxtPage />
			<template #fallback>
				<!-- 路由全屏 loading 已在时不再叠一层，只保留公共 PageLoading -->
				<PageLoading v-if="!isPageLoading" mode="inline" />
			</template>
		</Suspense>
	</NuxtLayout>
	<ClientOnly>
		<Teleport to="body">
			<PageLoading v-if="isPageLoading" mode="fullscreen" />
		</Teleport>
		<PcCandidateTagsDialog
			v-if="showCandidateTagsDialog"
			:auto-trigger-on-first-tab-switch="true"
		/>
		<TrackingDebugPanel />
	</ClientOnly>
</template>

<script setup>
import { isSandbox } from "~/utils/common";
import MonitorSDK from "~/libs/monitor-sdk.js";
import { appEnvInit, isInAppAsync } from "~/components/app.js";
import PageLoading from "~/components/page-loading/index.vue";

const { isPageLoading } = usePageLoading();
const showCandidateTagsDialog = ref(false);

const scheduleIdleTask = (task, timeout = 1200) => {
	if (!import.meta.client) return;

	if ("requestIdleCallback" in window) {
		window.requestIdleCallback(task, { timeout });
		return;
	}

	window.setTimeout(task, timeout);
};

const initVConsole = () => {
	if (!isSandbox && !/isShowVConsole=1/.test(window.location.search)) return;
	import("vconsole")
		.then(module => {
			const Vconsole = module.default;
			new Vconsole();
		})
		.catch(error => {
			console.warn("VConsole 加载失败:", error);
		});
};

const initMonitorSDK = () => {
	const config = useRuntimeConfig();
	const endpoint = isSandbox
		? "https://sandbox-error-log.seeronnet.com/api/error"
		: "https://error-log.seeronnet.com/api/error";

	MonitorSDK.init({
		endpoint: endpoint,
		appId: "nuxt.seer.gw.website",
		cdnUrl: config.public.cdnUrl || "",
		ignoreDomains: [
			"google.com",
			"google-analytics.com",
			"googletagmanager.com",
			"googleadservices.com",
			"connect.facebook.net",
			"facebook.com",
			"firebase.googleapis.com",
			"firebaseapp.com",
			"apis.google.com",
			"accounts.google.com",
			"doubleclick.net",
			"firebaseinstallations.googleapis.com",
			"tw-relayfe.seersecret.com",
			"www.clarity.ms",
			"clarity.ms",
			"line.me",
		],
		ignoreErrors: [
			"Failed to load facebook-jssdk SDK",
			"ResizeObserver loop limit exceeded",
			"Script error.",
		],
		ignoreStatusCodes: [401, 403],
		enable: {
			jsError: true,
			promiseRejection: false,
			resourceError: false,
			apiError: false,
		},
	});
};

const initPerformanceObserver = () => {
	if (
		!import.meta.client ||
		typeof window === "undefined" ||
		typeof PerformanceObserver === "undefined"
	) {
		return;
	}

	const reportMetric = (name, value, extra = {}) => {
		console.info(`[perf] ${name}`, {
			value,
			path: window.location.pathname,
			...extra,
		});
	};

	try {
		const paintObserver = new PerformanceObserver(list => {
			for (const entry of list.getEntries()) {
				if (entry.name === "first-contentful-paint") {
					reportMetric("FCP", entry.startTime);
				}
			}
		});
		paintObserver.observe({ type: "paint", buffered: true });
	} catch {}

	try {
		let lcpEntry = null;
		const lcpObserver = new PerformanceObserver(list => {
			const entries = list.getEntries();
			lcpEntry = entries[entries.length - 1];
		});
		lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
		document.addEventListener(
			"visibilitychange",
			() => {
				if (document.visibilityState === "hidden" && lcpEntry) {
					reportMetric("LCP", lcpEntry.startTime);
				}
			},
			{ once: true }
		);
	} catch {}

	try {
		let clsValue = 0;
		const clsObserver = new PerformanceObserver(list => {
			for (const entry of list.getEntries()) {
				if (!entry.hadRecentInput) {
					clsValue += entry.value;
				}
			}
		});
		clsObserver.observe({ type: "layout-shift", buffered: true });
		document.addEventListener(
			"visibilitychange",
			() => {
				if (document.visibilityState === "hidden") {
					reportMetric("CLS", clsValue);
				}
			},
			{ once: true }
		);
	} catch {}

	try {
		const navEntry = performance.getEntriesByType("navigation")?.[0];
		if (navEntry) {
			reportMetric("NAVIGATION", navEntry.duration, {
				type: navEntry.type,
				domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
			});
		}
	} catch {}
};

const initApp = async () => {
	const inApp = await isInAppAsync(3000);

	if (inApp) {
		try {
			await appEnvInit({
				appTitleBar: true,
				platform: "app_web",
			});
		} catch (error) {
			console.error("App 环境初始化失败:", error);
		}
	}
};

onMounted(() => {
	initApp();

	scheduleIdleTask(() => {
		showCandidateTagsDialog.value = true;
		// initMonitorSDK();
		initVConsole();
		initPerformanceObserver();
	});
});
</script>

<style>
@import "~/assets/scss/fonts.scss";
@import "~/assets/scss/common.scss";
@import "~/assets/scss/reset.scss";

body {
	/* background-color: #f6f6f6; */
}
</style>
